import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiClientService } from '../../../core/http/api-client.service';
import {
  CompanyItem,
  CompanyBranding,
  CompanyBrandingDto,
  CompanyItemDto,
  CompanyListSort,
  CompanyPage,
  CompanyPageDto,
  CompanyStatusFilter,
  BootstrapCompanyRequest,
  BootstrapCompanyResponseDto,
  BootstrapCompanyResult,
  UpdateCompanyBrandingRequest,
  UpdateCompanyRequest,
} from './companies.models';

@Injectable({
  providedIn: 'root'
})
export class CompaniesService
{
  private readonly apiClient = inject(ApiClientService);

  public getList(): Observable<CompanyItem[]>
  {
    return this.apiClient.get<CompanyItemDto[] | null>('/companies').pipe(
      map((items: CompanyItemDto[] | null) =>
      {
        if (!Array.isArray(items))
        {
          return [];
        }

        return items.map((item) => this.mapCompanyItem(item));
      })
    );
  }

  public getPage(params: {
    limit: number;
    cursor?: string | null;
    query?: string;
    status?: CompanyStatusFilter;
    sort?: CompanyListSort;
  }): Observable<CompanyPage> {
    return this.apiClient.get<CompanyPageDto>(
      '/companies',
      {
        limit: String(params.limit),
        cursor: params.cursor ?? undefined,
        q: params.query?.trim() || undefined,
        status: params.status || undefined,
        sort: params.sort ?? 'createdAtDesc',
      },
    ).pipe(
      map((page) => ({
        items: Array.isArray(page?.items) ? page.items.map((item) => this.mapCompanyItem(item)) : [],
        nextCursor: page?.nextCursor ?? null,
        hasMore: !!page?.hasMore,
      })),
    );
  }

  public getBySlug(slug: string): Observable<CompanyItem> {
    return this.apiClient.get<CompanyItemDto>(`/companies/by-slug/${encodeURIComponent(slug)}`).pipe(
      map((item) => this.mapCompanyItem(item)),
    );
  }

  public getBrandingBySlug(slug: string): Observable<CompanyBranding> {
    return this.apiClient.get<CompanyBrandingDto>(
      `/companies/by-slug/${encodeURIComponent(slug)}/branding`,
    ).pipe(
      map((item) => ({
        companyId: item.companyId,
        slug: item.slug,
        legalName: item.legalName,
        brandName: item.brandName,
        displayName: item.displayName,
        logoUrl: item.logoUrl,
        primaryColor: item.primaryColor,
        secondaryColor: item.secondaryColor,
        accentColor: item.accentColor,
        slotAvailableColor: item.slotAvailableColor ?? '#22c55e',
        slotUnavailableColor: item.slotUnavailableColor ?? '#f3f4f6',
        slotPressureColor: item.slotPressureColor ?? '#f59e0b',
        slotPastPatternColor: item.slotPastPatternColor ?? '#64748b',
        slotPastPatternOpacity: item.slotPastPatternOpacity ?? 20,
        sidebarBackgroundColor: item.sidebarBackgroundColor,
        sidebarTextColor: item.sidebarTextColor,
        sidebarBackgroundOverlayOpacity: item.sidebarBackgroundOverlayOpacity,
        sidebarBackgroundImageUrl: item.sidebarBackgroundImageUrl,
        contentBackgroundOverlayOpacity: item.contentBackgroundOverlayOpacity,
        contentBackgroundImageUrl: item.contentBackgroundImageUrl,
        topbarBackgroundOverlayOpacity: item.topbarBackgroundOverlayOpacity,
        topbarBackgroundImageUrl: item.topbarBackgroundImageUrl,
        showCompanyNameInHeader: item.showCompanyNameInHeader,
      })),
    );
  }

  public getCurrentBranding(companyId?: string): Observable<CompanyBranding> {
    return this.apiClient.get<CompanyBrandingDto>(
      '/companies/branding/current',
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item) => ({
        companyId: item.companyId,
        slug: item.slug,
        legalName: item.legalName,
        brandName: item.brandName,
        displayName: item.displayName,
        logoUrl: item.logoUrl,
        primaryColor: item.primaryColor,
        secondaryColor: item.secondaryColor,
        accentColor: item.accentColor,
        slotAvailableColor: item.slotAvailableColor ?? '#22c55e',
        slotUnavailableColor: item.slotUnavailableColor ?? '#f3f4f6',
        slotPressureColor: item.slotPressureColor ?? '#f59e0b',
        slotPastPatternColor: item.slotPastPatternColor ?? '#64748b',
        slotPastPatternOpacity: item.slotPastPatternOpacity ?? 20,
        sidebarBackgroundColor: item.sidebarBackgroundColor,
        sidebarTextColor: item.sidebarTextColor,
        sidebarBackgroundOverlayOpacity: item.sidebarBackgroundOverlayOpacity,
        sidebarBackgroundImageUrl: item.sidebarBackgroundImageUrl,
        contentBackgroundOverlayOpacity: item.contentBackgroundOverlayOpacity,
        contentBackgroundImageUrl: item.contentBackgroundImageUrl,
        topbarBackgroundOverlayOpacity: item.topbarBackgroundOverlayOpacity,
        topbarBackgroundImageUrl: item.topbarBackgroundImageUrl,
        showCompanyNameInHeader: item.showCompanyNameInHeader,
      })),
    );
  }

  public updateCurrentBranding(
    payload: UpdateCompanyBrandingRequest,
    companyId?: string,
  ): Observable<CompanyBranding> {
    return this.apiClient.patch<CompanyBrandingDto, UpdateCompanyBrandingRequest>(
      '/companies/branding/current',
      payload,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item) => ({
        companyId: item.companyId,
        slug: item.slug,
        legalName: item.legalName,
        brandName: item.brandName,
        displayName: item.displayName,
        logoUrl: item.logoUrl,
        primaryColor: item.primaryColor,
        secondaryColor: item.secondaryColor,
        accentColor: item.accentColor,
        slotAvailableColor: item.slotAvailableColor ?? '#22c55e',
        slotUnavailableColor: item.slotUnavailableColor ?? '#f3f4f6',
        slotPressureColor: item.slotPressureColor ?? '#f59e0b',
        slotPastPatternColor: item.slotPastPatternColor ?? '#64748b',
        slotPastPatternOpacity: item.slotPastPatternOpacity ?? 20,
        sidebarBackgroundColor: item.sidebarBackgroundColor,
        sidebarTextColor: item.sidebarTextColor,
        sidebarBackgroundOverlayOpacity: item.sidebarBackgroundOverlayOpacity,
        sidebarBackgroundImageUrl: item.sidebarBackgroundImageUrl,
        contentBackgroundOverlayOpacity: item.contentBackgroundOverlayOpacity,
        contentBackgroundImageUrl: item.contentBackgroundImageUrl,
        topbarBackgroundOverlayOpacity: item.topbarBackgroundOverlayOpacity,
        topbarBackgroundImageUrl: item.topbarBackgroundImageUrl,
        showCompanyNameInHeader: item.showCompanyNameInHeader,
      })),
    );
  }

  public uploadBrandingLogo(file: File, companyId?: string): Observable<CompanyBranding> {
    return this.uploadBrandingAsset('/companies/branding/current/logo', file, companyId);
  }

  public removeBrandingLogo(companyId?: string): Observable<CompanyBranding> {
    return this.removeBrandingAsset('/companies/branding/current/logo/remove', companyId);
  }

  public uploadSidebarBackground(file: File, companyId?: string): Observable<CompanyBranding> {
    return this.uploadBrandingAsset('/companies/branding/current/sidebar-background', file, companyId);
  }

  public removeSidebarBackground(companyId?: string): Observable<CompanyBranding> {
    return this.removeBrandingAsset('/companies/branding/current/sidebar-background/remove', companyId);
  }

  public uploadContentBackground(file: File, companyId?: string): Observable<CompanyBranding> {
    return this.uploadBrandingAsset('/companies/branding/current/content-background', file, companyId);
  }

  public removeContentBackground(companyId?: string): Observable<CompanyBranding> {
    return this.removeBrandingAsset('/companies/branding/current/content-background/remove', companyId);
  }

  public uploadTopbarBackground(file: File, companyId?: string): Observable<CompanyBranding> {
    return this.uploadBrandingAsset('/companies/branding/current/topbar-background', file, companyId);
  }

  public removeTopbarBackground(companyId?: string): Observable<CompanyBranding> {
    return this.removeBrandingAsset('/companies/branding/current/topbar-background/remove', companyId);
  }

  public bootstrap(payload: BootstrapCompanyRequest): Observable<BootstrapCompanyResult>
  {
    return this.apiClient
      .post<BootstrapCompanyResponseDto, BootstrapCompanyRequest>(
        '/companies/bootstrap',
        payload
      )
      .pipe(
        map((response) => ({
          company: this.mapCompanyItem(response.company),
          companyAdminEmail: response.companyAdmin.email,
          onboardingEmailQueued: !!response.onboardingEmailQueued,
        }))
      );
  }

  public updateEnabled(
    companyId: string,
    isEnabled: boolean,
    confirmSlug: string,
  ): Observable<CompanyItem> {
    return this.apiClient.patch<CompanyItemDto, { isEnabled: boolean; confirmSlug: string }>(
      `/companies/${companyId}/enabled`,
      { isEnabled, confirmSlug },
    ).pipe(map((item) => this.mapCompanyItem(item)));
  }

  public deleteCompany(companyId: string, confirmSlug: string): Observable<{ deleted: boolean }> {
    return this.apiClient.delete<{ deleted: boolean }>(
      `/companies/${companyId}`,
      { confirmSlug },
    );
  }

  public updateCompany(
    companyId: string,
    payload: UpdateCompanyRequest,
  ): Observable<CompanyItem> {
    return this.apiClient.patch<CompanyItemDto, UpdateCompanyRequest>(
      `/companies/${companyId}`,
      payload,
    ).pipe(map((item) => this.mapCompanyItem(item)));
  }

  private mapCompanyItem(item: CompanyItemDto): CompanyItem {
    return {
      companyId: item.id,
      slug: item.slug,
      legalName: item.legalName,
      brandName: item.brandName,
      isEnabled: item.isEnabled,
      companyAdminEmail: item.companyAdminEmail,
      companyAdminPhone: item.companyAdminPhone,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    };
  }

  private uploadBrandingAsset(path: string, file: File, companyId?: string): Observable<CompanyBranding> {
    const body = new FormData();
    body.append('file', file);

    return this.apiClient.post<CompanyBrandingDto, FormData>(
      path,
      body,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item) => ({
        companyId: item.companyId,
        slug: item.slug,
        legalName: item.legalName,
        brandName: item.brandName,
        displayName: item.displayName,
        logoUrl: item.logoUrl,
        primaryColor: item.primaryColor,
        secondaryColor: item.secondaryColor,
        accentColor: item.accentColor,
        slotAvailableColor: item.slotAvailableColor ?? '#22c55e',
        slotUnavailableColor: item.slotUnavailableColor ?? '#f3f4f6',
        slotPressureColor: item.slotPressureColor ?? '#f59e0b',
        slotPastPatternColor: item.slotPastPatternColor ?? '#64748b',
        slotPastPatternOpacity: item.slotPastPatternOpacity ?? 20,
        sidebarBackgroundColor: item.sidebarBackgroundColor,
        sidebarTextColor: item.sidebarTextColor,
        sidebarBackgroundOverlayOpacity: item.sidebarBackgroundOverlayOpacity,
        sidebarBackgroundImageUrl: item.sidebarBackgroundImageUrl,
        contentBackgroundOverlayOpacity: item.contentBackgroundOverlayOpacity,
        contentBackgroundImageUrl: item.contentBackgroundImageUrl,
        topbarBackgroundOverlayOpacity: item.topbarBackgroundOverlayOpacity,
        topbarBackgroundImageUrl: item.topbarBackgroundImageUrl,
        showCompanyNameInHeader: item.showCompanyNameInHeader,
      })),
    );
  }

  private removeBrandingAsset(path: string, companyId?: string): Observable<CompanyBranding> {
    return this.apiClient.patch<CompanyBrandingDto, Record<string, never>>(
      path,
      {},
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item) => ({
        companyId: item.companyId,
        slug: item.slug,
        legalName: item.legalName,
        brandName: item.brandName,
        displayName: item.displayName,
        logoUrl: item.logoUrl,
        primaryColor: item.primaryColor,
        secondaryColor: item.secondaryColor,
        accentColor: item.accentColor,
        slotAvailableColor: item.slotAvailableColor ?? '#22c55e',
        slotUnavailableColor: item.slotUnavailableColor ?? '#f3f4f6',
        slotPressureColor: item.slotPressureColor ?? '#f59e0b',
        slotPastPatternColor: item.slotPastPatternColor ?? '#64748b',
        slotPastPatternOpacity: item.slotPastPatternOpacity ?? 20,
        sidebarBackgroundColor: item.sidebarBackgroundColor,
        sidebarTextColor: item.sidebarTextColor,
        sidebarBackgroundOverlayOpacity: item.sidebarBackgroundOverlayOpacity,
        sidebarBackgroundImageUrl: item.sidebarBackgroundImageUrl,
        contentBackgroundOverlayOpacity: item.contentBackgroundOverlayOpacity,
        contentBackgroundImageUrl: item.contentBackgroundImageUrl,
        topbarBackgroundOverlayOpacity: item.topbarBackgroundOverlayOpacity,
        topbarBackgroundImageUrl: item.topbarBackgroundImageUrl,
        showCompanyNameInHeader: item.showCompanyNameInHeader,
      })),
    );
  }
}
